import React, { forwardRef, useRef, useImperativeHandle } from 'react';

import {
  announceToScreenReader,
  keyboardNavigation,
} from '../../utils/accessibility';
import { cn } from '../../utils/cn';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?:
    | 'primary'
    | 'secondary'
    | 'outline'
    | 'ghost'
    | 'danger'
    | 'success'
    | 'icon-primary'
    | 'icon-secondary'
    | 'icon-danger';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  loadingText?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  rounded?: boolean;
  // Accessibility props
  'aria-describedby'?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?:
    | boolean
    | 'false'
    | 'true'
    | 'menu'
    | 'listbox'
    | 'tree'
    | 'grid'
    | 'dialog';
  'aria-controls'?: string;
  'aria-pressed'?: boolean;
  announceClick?: boolean;
  announceMessage?: string;
  skipFocusRing?: boolean;
}

const variants = {
  primary:
    'bg-primary-800 text-white hover:bg-primary-900 focus:ring-strat-routeTeal disabled:bg-primary-400',
  secondary:
    'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 disabled:bg-gray-300',
  outline:
    'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-300',
  ghost:
    'text-gray-700 hover:bg-gray-100 focus:ring-gray-500 disabled:text-gray-300',
  danger:
    'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-300',
  success:
    'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 disabled:bg-green-300',
  'icon-primary':
    'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-blue-300 p-2 min-w-0',
  'icon-secondary':
    'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 disabled:bg-gray-300 p-2 min-w-0',
  'icon-danger':
    'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-300 p-2 min-w-0',
};

const sizes = {
  sm: 'px-3 py-1.5 text-sm',
  md: 'px-4 py-2 text-sm',
  lg: 'px-6 py-3 text-base',
};

export interface ButtonRef {
  focus: () => void;
  blur: () => void;
  click: () => void;
}

export const Button = forwardRef<ButtonRef, ButtonProps>(
  (
    {
      children,
      variant = 'primary',
      size = 'md',
      loading = false,
      loadingText,
      icon,
      iconPosition = 'left',
      fullWidth = false,
      rounded = false,
      className,
      disabled,
      onClick,
      announceClick = false,
      announceMessage,
      skipFocusRing = false,
      'aria-describedby': ariaDescribedBy,
      'aria-expanded': ariaExpanded,
      'aria-haspopup': ariaHasPopup,
      'aria-controls': ariaControls,
      'aria-pressed': ariaPressed,
      ...props
    },
    ref
  ) => {
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Expose button methods through ref
    useImperativeHandle(ref, () => ({
      focus: () => buttonRef.current?.focus(),
      blur: () => buttonRef.current?.blur(),
      click: () => buttonRef.current?.click(),
    }));

    const isDisabled = disabled || loading;

    // Enhanced click handler with accessibility announcements
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return;
      }

      // Announce click action to screen readers if requested
      if (announceClick) {
        const message =
          announceMessage ||
          (loading ? 'Action in progress' : 'Button activated');
        announceToScreenReader(message, 'polite');
      }

      onClick?.(event);
    };

    // Enhanced keyboard handler
    const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (isDisabled) {
        event.preventDefault();
        return;
      }

      // Handle activation (Enter/Space)
      keyboardNavigation.handleActivation(event.nativeEvent, () => {
        buttonRef.current?.click();
      });

      props.onKeyDown?.(event);
    };

    // Loading spinner component
    const LoadingSpinner = () => (
      <svg
        className="animate-spin h-4 w-4"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    );

    // Get display text for screen readers
    const getScreenReaderText = () => {
      if (loading) {
        return loadingText || 'Loading';
      }
      if (typeof children === 'string') {
        return children;
      }
      return 'Button'; // Fallback for complex children
    };

    const baseClasses = cn(
      // Base styles
      'inline-flex items-center justify-center font-medium transition-colors duration-200',
      'border border-transparent',

      // Focus styles (unless skipped)
      !skipFocusRing && 'focus:outline-none focus:ring-2 focus:ring-offset-2',

      // Size
      sizes[size],

      // Variant
      variants[variant],

      // Shape
      rounded ? 'rounded-full' : 'rounded-md',

      // Width
      fullWidth && 'w-full',

      // Disabled state
      isDisabled && 'cursor-not-allowed opacity-60',

      // Custom classes
      className
    );

    return (
      <button
        ref={buttonRef}
        className={baseClasses}
        disabled={isDisabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-disabled={isDisabled}
        aria-busy={loading}
        aria-describedby={ariaDescribedBy}
        aria-expanded={ariaExpanded}
        aria-haspopup={ariaHasPopup}
        aria-controls={ariaControls}
        aria-pressed={ariaPressed}
        aria-label={props['aria-label']}
        role={props.role || 'button'}
        tabIndex={isDisabled ? -1 : props.tabIndex}
        {...props}
      >
        {/* Loading state */}
        {loading && (
          <>
            <LoadingSpinner />
            <span className="ml-2">{loadingText || 'Loading...'}</span>
            <span className="sr-only">
              Please wait, {getScreenReaderText()} is processing
            </span>
          </>
        )}

        {/* Normal state */}
        {!loading && (
          <>
            {/* Left icon */}
            {icon && iconPosition === 'left' && (
              <span
                className={cn('flex-shrink-0', children ? 'mr-2' : '')}
                aria-hidden="true"
              >
                {icon}
              </span>
            )}

            {/* Button content */}
            {children && (
              <span className={loading ? 'sr-only' : ''}>{children}</span>
            )}

            {/* Right icon */}
            {icon && iconPosition === 'right' && (
              <span
                className={cn('flex-shrink-0', children ? 'ml-2' : '')}
                aria-hidden="true"
              >
                {icon}
              </span>
            )}
          </>
        )}

        {/* Screen reader only status */}
        {isDisabled && !loading && <span className="sr-only">(disabled)</span>}
      </button>
    );
  }
);

Button.displayName = 'Button';
